import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import HowToPlayVideo from "@/components/HowToPlayVideo";
import Avatar2D from "@/components/Avatar2D";
import BotFace from "@/components/BotFace";
import InductionSteps from "@/components/InductionSteps";
import { markFirstGameGuideSeen } from "@/components/FirstGameGuide";
import { myCharacter } from "@/lib/avatarConfig";
import { ArrowRight } from "@phosphor-icons/react";

const RUFUS = { id: "rookie", name: "Rookie Rufus", elo: 800 };
const DEMO_BOARD = "boardgame3"; // Classic

// Steps 2 and 3 of the new player induction (step 1 is /avatar?welcome=1):
// /welcome?step=video, then /welcome?step=game.
export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const step = params.get("step") === "game" ? 3 : 2;
  const [starting, setStarting] = useState(false);

  const toGame = () => {
    markFirstGameGuideSeen(user);
    navigate("/welcome?step=game", { replace: true });
  };

  const startDemo = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/match/find", {
        mode: "turn_based", opponent: "ai", layout: DEMO_BOARD, time_control_seconds: 0,
        bot_id: RUFUS.id, bot_elo: RUFUS.elo, bot_name: RUFUS.name,
      });
      if (data.status === "matched") {
        try { localStorage.setItem(`jangle:tutorial-match:${data.match_id}`, "1"); } catch { /* ignore */ }
        navigate(`/match/${data.match_id}?layout=${DEMO_BOARD}`, { replace: true });
        return;
      }
      toast.info("Finish your current game first.");
      navigate("/lobby", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't start the demo game");
    } finally {
      setStarting(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 md:px-10 py-6 md:py-10" data-testid={`welcome-step-${step}`}>
      <InductionSteps step={step} />
      {step === 2 ? (
        <>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">How to play</h1>
          <p className="text-muted-foreground mt-2 mb-5">
            A two minute tour of Jangle. You can watch it again any time under How to play.
          </p>
          <HowToPlayVideo character={myCharacter(user)} onFinish={toGame} finishLabel="Next: demo game" onSkip={toGame} />
          <div className="mt-5 flex justify-end">
            <Button onClick={toGame} className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="welcome-video-next">
              Next: demo game <ArrowRight size={16} weight="bold" className="ml-1" />
            </Button>
          </div>
        </>
      ) : (
        <div className="bg-surface border border-border rounded-2xl p-6 md:p-10 text-center">
          <div className="flex items-end justify-center gap-6 mb-5">
            <Avatar2D character={myCharacter(user)} flag="#f393a1" className="w-28 md:w-36" />
            <span className="font-display font-black text-2xl text-muted-foreground pb-10">vs</span>
            <BotFace name={RUFUS.name} className="w-24 h-24 md:w-28 md:h-28 mb-4 ring-2 ring-emerald-500/40" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Your demo game</h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Play Rookie Rufus, our friendliest bot, on the Classic board. Take your time: it's turn based
            and doesn't change your Elo.
          </p>
          <Button
            onClick={startDemo}
            disabled={starting}
            size="lg"
            className="btn-tile mt-6 h-12 px-8 rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground text-base"
            data-testid="welcome-start-demo"
          >
            {starting ? "Starting…" : "Play Rookie Rufus"}
            <ArrowRight size={18} weight="bold" className="ml-1.5" />
          </Button>
        </div>
      )}
    </main>
  );
}
